import React, { useEffect, useState, useRef } from 'react'
import './Home.css';
import ProfileSide from '../../components/ProfileSide/ProfileSide';
import Main from '../../components/Main/Main';
import Header from '../../components/Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { useDisclosure } from '@mantine/hooks';
import { Modal, useMantineTheme } from '@mantine/core';
import usePageTracking from '../../hooks/usePageTracking';
import { fetch_theme } from '../../api/ItemManipulation';
import { setTheme } from '../../actions/ThemeAction';
import BackToTopButton from '../../components/Main/Button/BackToTopButton';
import { 
  openItemModalAction,
  openDatumModalAction, 
  openUserModalAction,
  loadModal
} from '../../actions/ModalAction';
import { checkDeepLinkAccess } from '../../api/DeepLinkRequest';

const Home = () => {
    usePageTracking('ns_home');
    const [opened, { open, close }] = useDisclosure(false);
    const theme = useMantineTheme();

    const dispatch = useDispatch();
    const { modalData, rModalOpened, loading } = useSelector((state) => state.modalReducer);
    const { user } = useSelector((state) => state.authReducer.authData);
    const deepLinkIntent = useSelector((state) => state.authReducer.deepLinkIntent);

    const deepLinkHandledRef = useRef(false);

    useEffect(() => {
        if (!deepLinkIntent || !deepLinkIntent.id || !user) return;
        if (deepLinkHandledRef.current) return;

        deepLinkHandledRef.current = true;
        handleDeepLink(deepLinkIntent);
    }, [deepLinkIntent, user]);

    useEffect(() => {
        if (!deepLinkIntent) {
            deepLinkHandledRef.current = false;
        }
    }, [deepLinkIntent]);

    const handleDeepLink = async (intent) => {
        if (user.userType !== 0) {
            dispatch({type: "MODAL_ITEM_VIEW_SUCCESS", data: {
                modalType: "info",
                title: 'Feature Not Available',
                message: 'Deep link viewing is currently only available for General Users. Log in with a General User account to view shared content.'
            }})
            dispatch({ type: 'CLEAR_DEEP_LINK_INTENT' });
            return;
        }
      try {
          switch (intent.type) {
              case 'item': {
                  const res = await checkDeepLinkAccess(intent.id, 'item', user._id);
                  if (!res.data.hasAccess) {
                    dispatch({type: "MODAL_ITEM_VIEW_SUCCESS", data: {
                        modalType: "info",
                        title: 'Item Not Available',
                        message: 'This item is not available in your current feed. Follow the Groups that reference it to see it.'
                    }})
                    dispatch({ type: 'CLEAR_DEEP_LINK_INTENT' });
                    return;
                  }
                  const logDetails = {
                    activity: "deep_link_item_view",
                    category: 'interaction',
                    userId: user._id,
                    data: {
                      userId: user._id,
                      username: user.username,
                    },
                    response: null,
                    sessionId: localStorage.getItem('sessionId'),
                    status: null,
                  };
                  dispatch(openItemModalAction(intent.id, user._id, logDetails, intent.tab, intent.filter));
                  break;
              }
              case 'datum': {
                  const res = await checkDeepLinkAccess(intent.id, 'datum', user._id);
                  if (!res.data.hasAccess) {
                    dispatch({type: "MODAL_ITEM_VIEW_SUCCESS", data: {
                        modalType: "info",
                        title: 'Datum Not Available',
                        message: 'This datum is not available in your current feed. Follow the Groups that have stamped it to see it.'

                    }})
                    dispatch({ type: 'CLEAR_DEEP_LINK_INTENT' });
                    return;
                  }
                  const logDetails = {
                    activity: "deep_link_datum_view",
                    category: "interaction",
                    userId: user._id,
                    data: {
                        userId: user._id,
                        username: user.username,
                    },
                    response: null,
                    sessionId: localStorage.getItem('sessionId'),
                    status: null
                  }
                  dispatch(openDatumModalAction(intent.id, user._id, logDetails));
                  break;
              }
              case 'user': {
                  const logDetails = {
                    userId: user._id,
                    activity: "deep_link_user_view",
                    category: 'interaction',
                    sessionId: localStorage.getItem('sessionId'),
                    data : {
                        userId: user._id,
                        username: user.username,
                    },
                  }
                  dispatch(openUserModalAction(intent.id, intent.tab, logDetails));
                  break;
              }
              default:
                  break;
          }
      } catch (error) {
          console.error('Deep link handling error:', error);
      } finally {
        dispatch({ type: 'CLEAR_DEEP_LINK_INTENT' });
    }
    };

    // Resend cooldown state
    const [cooldown, setCooldown] = useState(0);
    const cooldownRef = useRef(null);

    const handleResend = async () => {
        if (cooldown > 0) return;

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/resend-verification/${user._id}`, {
                method: 'POST',
                credentials: 'include'
            });

            // Start 60 second countdown
            setCooldown(60);
            cooldownRef.current = setInterval(() => {
                setCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(cooldownRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (error) {
            console.error('Resend failed:', error);
        }
    };

    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    useEffect(() => {
        open();
    }, [loading]);

    const init = async () => {
        const res = await fetch_theme();
        dispatch(setTheme(res.data));
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (rModalOpened === true) {
            open();
        } else {
            close();
        }
    }, [rModalOpened]);

    return (
        <div className="Home HeaderGrid">
            <a href="#top" id="top"></a>
            <div className='home-header-container'>
                <Header location="home" />
            </div>
            {!user.emailVerified && (
                <div style={{
                    background: '#fffbeb',
                    border: '1px solid #f59e0b',
                    borderRadius: 4,
                    padding: '6px 12px',
                    fontSize: 12,
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8
                }}>
                    <span>📧 Please verify your email address to secure your account.</span>
                    <button
                        style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            cursor: cooldown > 0 ? 'not-allowed' : 'pointer',
                            opacity: cooldown > 0 ? 0.6 : 1,
                            minWidth: 90,
                            transition: 'opacity 0.2s'
                        }}
                        onClick={handleResend}
                        disabled={cooldown > 0}
                    >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Email'}
                    </button>
                </div>
            )}
            <div className='BodyGrid'>
                <ProfileSide />
                <Main />
            </div>
            <BackToTopButton />
        </div>
    );
};

export default Home;